import { ControllerProps, FieldValues } from "react-hook-form";
import { SelectionOption } from "../../../types";
import { useField, useOptions } from "../../../hooks";
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  Chip,
  ChipProps,
  CircularProgress,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { ComponentType, HTMLAttributes } from "react";

/**
 * Props for the SplitButton component.
 * @template TOption - The type of the options.
 */
export type ControlledMultiselectProps<
  TOption extends SelectionOption,
  TForm extends FieldValues
> = Required<Pick<ControllerProps<TForm>, "control" | "name">> &
  Omit<
    AutocompleteProps<TOption, true, undefined, false>,
    "options" | "renderInput"
  > & {
    /**
     * Can be:
     * - An array of options.
     * - A function that returns an array of options.
     * - A function that returns a Promise that resolves to an array of options.
     */
    getOptions: TOption[] | (() => TOption[]) | (() => Promise<TOption[]>); // TODO: debería aceptar opciones con tipo value distintos de number
    TagComponent?: ComponentType<ChipProps & { value: TOption }>;
    searchable?: boolean;
  } & Pick<TextFieldProps, "required" | "label" | "variant" | "size">;

export function ControlledMultiselect<
  TOption extends SelectionOption,
  TForm extends FieldValues
>(props: ControlledMultiselectProps<TOption, TForm>) {
  const {
    control,
    name,
    getOptions,
    label,
    disabled,
    required,
    variant,
    size,
    TagComponent,
    searchable,
    ...rest
  } = props;

  const { field, fieldState, formState } = useField<TOption[], TForm>({
    name,
    control,
  });

  const { options, isSuccess, isLoading } = useOptions<TOption>({
    getOptions,
  });

  return (
    <Autocomplete
      multiple
      options={options ? options : []}
      loading={isLoading}
      value={field.value || []}
      onChange={(event, newValue) => {
        field.onChange(newValue || undefined);
      }}
      
      // getOptionKey={(option) => option.value}

      renderOption={(props: HTMLAttributes<HTMLLIElement>, option: TOption, state: AutocompleteRenderOptionState) => {
        return (
          <MenuItem {...props}>
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        );
      }}
      isOptionEqualToValue={(option: TOption, value: TOption) =>
        option.value === value.value
      }
      filterOptions={
        searchable
          ? (options, state) =>
              options.filter(
                (option) =>
                  option.label
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase())
                // TODO: se puede mejorar el filtro para que sea de tipo "localeContains"
              )
          : undefined
      }
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={props.label}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            required={props.required}
            variant={variant}
            size={size}
            inputProps={{
              ...params.inputProps,
              readOnly: !searchable,
              style: {
                cursor: !searchable ? "pointer" : undefined,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              // value: field.value?.label,
              sx: {
                cursor: !searchable ? "pointer" : undefined,
              },
            }}
          />
        );
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => {
          if (TagComponent) {
            return <TagComponent value={option} {...getTagProps({ index })} />;
          }

          return (
            <Chip
              variant="outlined"
              label={option.label}
              {...getTagProps({ index })}
            />
          );
        });
      }}
      {...rest}
    />
  );
}
